import { useEffect, useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { LANGUAGES } from "../i18n/translations";
import { WHATSAPP_URL, trackWhatsAppConversion } from "../data/mediaAssets";

const Header = () => {
    const { t, lang, setLang } = useI18n();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [langOpen, setLangOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const nav = [
        { id: "home", label: t.nav.home },
        { id: "about", label: t.nav.about },
        { id: "tours", label: t.nav.tours },
        { id: "gallery", label: t.nav.gallery },
        { id: "contact", label: t.nav.contact },
    ];

    const current = LANGUAGES.find((l) => l.code === lang);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-white/95 backdrop-blur-xl border-b border-neutral-200 shadow-[0_1px_20px_rgba(11,27,61,0.06)]"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
                {/* Brand */}
                <a
                    href="#home"
                    data-testid="brand-logo"
                    className="flex items-baseline gap-2 group"
                >
                    <span
                        className={`font-serif text-2xl md:text-3xl tracking-tight leading-none transition-colors ${
                            scrolled ? "text-brand-navy" : "text-white"
                        }`}
                    >
                        CH
                    </span>
                    <span
                        className={`font-sans text-[10px] md:text-xs uppercase tracking-[0.4em] transition-colors ${
                            scrolled ? "text-brand-gold" : "text-brand-gold"
                        }`}
                    >
                        Tours
                    </span>
                </a>

                {/* Desktop Nav */}
                <nav className="hidden lg:flex items-center gap-10">
                    {nav.map((item) => (
                        <a
                            key={item.id}
                            href={`#${item.id}`}
                            data-testid={`nav-link-${item.id}`}
                            className={`font-sans text-sm tracking-wide transition-colors relative group ${
                                scrolled
                                    ? "text-brand-navy hover:text-brand-gold"
                                    : "text-white/90 hover:text-brand-gold"
                            }`}
                        >
                            {item.label}
                            <span className="absolute -bottom-1 left-0 h-px w-0 bg-brand-gold transition-all duration-300 group-hover:w-full" />
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-3 md:gap-5">
                    {/* Language switcher */}
                    <div className="relative">
                        <button
                            data-testid="language-switcher-button"
                            onClick={() => setLangOpen((v) => !v)}
                            onBlur={() => setTimeout(() => setLangOpen(false), 150)}
                            className={`flex items-center gap-2 px-3 py-2 border transition-colors ${
                                scrolled
                                    ? "border-neutral-300 text-brand-navy hover:border-brand-gold"
                                    : "border-white/40 text-white hover:border-brand-gold"
                            }`}
                        >
                            <span className="text-base leading-none">{current?.flag}</span>
                            <span className="hidden sm:inline text-xs uppercase tracking-widest font-sans">
                                {current?.code}
                            </span>
                            <ChevronDown className="w-3 h-3" />
                        </button>
                        {langOpen && (
                            <div
                                data-testid="language-dropdown"
                                className="absolute right-0 mt-2 w-48 bg-white border border-neutral-200 shadow-xl py-2 animate-fade-in"
                            >
                                {LANGUAGES.map((l) => (
                                    <button
                                        key={l.code}
                                        data-testid={`language-option-${l.code}`}
                                        onMouseDown={() => {
                                            setLang(l.code);
                                            setLangOpen(false);
                                        }}
                                        className={`w-full text-left px-4 py-2 flex items-center gap-3 text-sm hover:bg-brand-cream transition-colors ${
                                            lang === l.code ? "text-brand-gold font-semibold" : "text-brand-navy"
                                        }`}
                                    >
                                        <span className="text-lg">{l.flag}</span>
                                        <span className="font-sans">{l.label}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <a
                        href={WHATSAPP_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={trackWhatsAppConversion}
                        data-testid="header-cta-book"
                        className="hidden md:inline-flex items-center bg-brand-gold hover:bg-brand-gold-hover text-white font-sans text-xs uppercase tracking-widest px-5 py-3 transition-all"
                    >
                        {t.nav.book}
                    </a>

                    {/* Mobile menu */}
                    <button
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen((v) => !v)}
                        className={`lg:hidden p-2 ${scrolled ? "text-brand-navy" : "text-white"}`}
                        aria-label="Menu"
                    >
                        {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>

            {/* Mobile drawer */}
            {open && (
                <div
                    data-testid="mobile-menu"
                    className="lg:hidden bg-white border-t border-neutral-100 animate-fade-in"
                >
                    <div className="px-6 py-6 flex flex-col gap-5">
                        {nav.map((item) => (
                            <a
                                key={item.id}
                                href={`#${item.id}`}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-link-${item.id}`}
                                className="font-sans text-base text-brand-navy hover:text-brand-gold transition-colors"
                            >
                                {item.label}
                            </a>
                        ))}
                        <a
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={trackWhatsAppConversion}
                            data-testid="mobile-cta-book"
                            className="mt-2 bg-brand-gold hover:bg-brand-gold-hover text-white font-sans text-xs uppercase tracking-widest px-5 py-3 text-center"
                        >
                            {t.nav.book}
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;
